import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  ControlledImageUpload,
  ControlledSelect,
  ControlledTextField,
  ErrorSummary,
  FormGrid,
  SectionTitle,
} from 'modules/shared/components';
import { AADHAAR_REGEX, PAN_REGEX } from 'modules/shared/constants';
import { calculateAge } from 'modules/shared/utils';

const CURRENT_ROUND_KEY = 'onboarding';

export default function DataEntryRound({ data, readOnly, onApprove }) {
  const methods = useForm<any>({
    defaultValues: { personalData: {} },
  });
  const { watch, setValue, handleSubmit, reset, trigger } = methods;
  const birthDate = watch('personalData.dateOfBirth');

  useEffect(() => {
    if (data) reset({ personalData: data });
  }, [data, reset]);

  useEffect(() => {
    const age = calculateAge(birthDate);
    if (age !== '') setValue('personalData.age', age, { shouldValidate: true });
    else setValue('personalData.age', '');
  }, [birthDate, setValue]);

  const photoUploadRef = useRef(null);

  const formColumnConfig = [
    {
      rows: [
        {
          type: 'customRow',
          render: () => (
            <div className='data-grid-row'>
              <Box
                sx={{
                  width: '100px',
                  height: '100px',
                  borderRadius: 1,
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
                  color: (theme) => alpha(theme.palette.grey[500], 0.5),
                  '& button': { position: 'absolute', bottom: 4, right: 4 },
                }}
              >
                {data?.personalData?.photo?.url ? (
                  <img src={data.personalData.photo.url} alt='' />
                ) : (
                  <Icon icon='fluent:person-16-filled' width={80} />
                )}
                {!readOnly && (
                  <button
                    type='button'
                    className='btn text-btn'
                    disabled={readOnly}
                    onClick={() => photoUploadRef.current?.openUploader()}
                  >
                    <Icon icon='line-md:edit-twotone' />
                  </button>
                )}
              </Box>
              <button
                type='button'
                className='btn cta-outlined-btn self-center justify-self-end'
                onClick={() => photoUploadRef.current?.openUploader()}
              >
                Upload photo
              </button>
            </div>
          ),
        },
        {
          label: 'Full name',
          field: (
            <ControlledTextField
              id='name'
              name='personalData.name'
              disabled={readOnly}
              type='text'
              placeholder='Enter full name'
              rules={{
                required: 'Full name is required',
                maxLength: { value: 100, message: 'Maximum 100 characters allowed' },
              }}
            />
          ),
        },
        {
          label: 'Date of birth',
          field: (
            <ControlledTextField
              id='dateOfBirth'
              name='personalData.dateOfBirth'
              disabled={readOnly}
              type='date'
              placeholder='Date of birth'
              rules={{
                required: 'Date of birth is required',
                validate: (value) => {
                  const dob = new Date(value);
                  const today = new Date();

                  if (isNaN(dob.getTime())) return 'Date of birth is invalid';
                  if (dob > today) return 'Date of birth cannot be in the future';

                  const age = calculateAge(value);
                  if (Number(age) < 18) return 'Candidate must be at least 18 years old';
                  if (Number(age) > 65) return 'Candidate must be at most 65 years old';
                  return true;
                },
              }}
            />
          ),
        },
        {
          label: 'Age',
          field: (
            <ControlledTextField
              id='age'
              name='personalData.age'
              disabled={true}
              type='number'
              placeholder='Age'
              rules={{ required: 'Age is required' }}
            />
          ),
        },
        {
          label: 'Gender',
          field: (
            <ControlledSelect
              id='gender'
              name='personalData.gender'
              placeholder='Select gender'
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
              ]}
              disabled={readOnly}
              rules={{ required: 'Gender is required' }}
            />
          ),
        },
      ],
    },
    {
      rows: [
        {
          label: 'Contact number',
          field: (
            <ControlledTextField
              id='contact'
              name='personalData.contact'
              disabled={readOnly}
              type='tel'
              placeholder='Enter contact number'
              rules={{
                required: 'Contact number is required',
                minLength: { value: 10, message: 'Contact number must be 10 digits' },
                maxLength: { value: 10, message: 'Contact number must be 10 digits' },
              }}
            />
          ),
        },
        {
          label: 'Email ID',
          field: (
            <ControlledTextField
              id='email'
              name='personalData.email'
              disabled={readOnly}
              type='email'
              placeholder='Enter email ID'
              rules={{ required: 'Email ID is required' }}
            />
          ),
        },
        {
          label: 'Residential address',
          field: (
            <ControlledTextField
              id='residentialAddress'
              name='personalData.residentialAddress'
              disabled={readOnly}
              type='text'
              placeholder='Enter residential address'
              rules={{ required: 'Residential address is required' }}
            />
          ),
        },
        {
          label: 'Permanent address',
          field: (
            <ControlledTextField
              id='permanentAddress'
              name='personalData.permanentAddress'
              disabled={readOnly}
              type='text'
              placeholder='Enter permanent address'
              rules={{ required: 'Permanent address is required' }}
            />
          ),
        },
        {
          label: 'Nationality',
          field: (
            <ControlledTextField
              id='nationality'
              name='personalData.nationality'
              disabled={readOnly}
              type='text'
              placeholder='Enter nationality'
              rules={{ required: 'Nationality is required' }}
            />
          ),
        },
        {
          label: 'Medical condition (if any)',
          field: (
            <ControlledTextField
              id='medicalCondition'
              name='personalData.medicalCondition'
              disabled={readOnly}
              type='text'
              placeholder='Enter medical condition'
            />
          ),
        },
        {
          label: 'Aadhaar number',
          field: (
            <ControlledTextField
              id='aadhaarNumber'
              name='personalData.aadhaarNumber'
              disabled={readOnly}
              type='text'
              placeholder='Enter Aadhaar number'
              rules={{
                required: 'Aadhaar number is required',
                minLength: { value: 12, message: 'Aadhaar number must be 12 digits' },
                maxLength: { value: 12, message: 'Aadhaar number must be 12 digits' },
                pattern: {
                  value: AADHAAR_REGEX,
                  message: `Invalid Aadhaar number. Cannot start with 0 or 1`,
                },
              }}
            />
          ),
        },
        {
          label: 'PAN number',
          field: (
            <ControlledTextField
              id='panNumber'
              name='personalData.panNumber'
              disabled={readOnly}
              type='text'
              placeholder='Enter PAN number'
              rules={{
                required: 'PAN number is required',
                minLength: { value: 10, message: 'PAN number must be 10 characters' },
                maxLength: { value: 10, message: 'PAN number must be 10 characters' },
                pattern: {
                  value: PAN_REGEX,
                  message: 'Invalid PAN number',
                },
              }}
            />
          ),
        },
      ],
    },
  ];

  const handleShare = async () => {
    onApprove?.({
      action: 'share',
      personalData: { formStatus: 'shared' },
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const onFormSubmit = async (data) => {
    const isFormValid = await trigger(['personalData'], { shouldFocus: true });
    if (!isFormValid) return;

    onApprove?.({
      action: 'submit',
      personalData: data.personalData,
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  return (
    <div className='column gap-3'>
      <SectionTitle title='ON-BOARDING PROCESS - PERSONAL DATA ENTRY TO OUR BOARD' />
      <button type='button' className='btn cta-outlined-btn self-end' onClick={handleShare}>
        <Icon icon='uil:share' width={18} />
        <span>Share form to candidate</span>
      </button>

      <FormProvider {...methods}>
        <form className='column gap-3' onSubmit={handleSubmit(onFormSubmit)}>
          <SectionTitle title='Personal details' variant='subtitle' />
          <ErrorSummary />
          <Box sx={{ display: 'none' }}>
            <ControlledImageUpload
              ref={photoUploadRef}
              name='personalData.photo'
              showPreview={false}
              buttonText='Upload photo'
              aspectRatio='1:1'
            />
          </Box>

          <FormGrid columns={formColumnConfig} />
          {!readOnly && (
            <button type='submit' className='btn cta-btn self-end'>
              Next
            </button>
          )}
        </form>
      </FormProvider>
    </div>
  );
}
